// Ties the browser wasm engine to the cached provisioning data so usePreview
// can price LOCALLY (spec §11). Process-level singletons: the engine is
// instantiated once from its hashed asset; provisioning loads from IndexedDB,
// falling back to an online fetch the first time (must-be-online-once).

import { getAccessToken } from "@awtoban/shared";

import type { ProvisioningBlob } from "../offline/db";
import { loadProvisioning, makeDistanceLookup, saveProvisioning } from "../offline/provisioning";
import wasmUrl from "./pricing_core.wasm?url";
import { createEngine, type PricingEngine } from "./wasmEngine";

export interface Provisioned {
  engine: PricingEngine;
  snapshotJson: string;
  distanceFor: (a: number, b: number) => string | null;
  version: string;
  homeCityId: number | null;
}

let enginePromise: Promise<PricingEngine> | null = null;

function getEngine(): Promise<PricingEngine> {
  if (enginePromise === null) {
    enginePromise = fetch(wasmUrl)
      .then((r) => r.arrayBuffer())
      .then(createEngine);
  }
  return enginePromise;
}

interface SnapshotResponse {
  tariff_version: string;
  engine_version: string;
  wasm_hash: string | null;
  data_version: string;
  snapshot: unknown;
  cities: unknown[];
}

/** Pull the snapshot + distance matrix from the server and cache them. */
export async function fetchProvisioningOnline(): Promise<ProvisioningBlob | null> {
  const token = getAccessToken();
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
  const [snapResp, distResp] = await Promise.all([
    fetch("/api/v1/pricing/snapshot", { headers }),
    fetch("/api/v1/pricing/distance-matrix", { headers }),
  ]);
  if (!snapResp.ok || !distResp.ok) {
    return null;
  }
  const snap = (await snapResp.json()) as SnapshotResponse;
  const dist = (await distResp.json()) as Array<{
    from_id: number;
    to_id: number;
    distance_km: string;
  }>;
  const blob: ProvisioningBlob = {
    version: snap.tariff_version,
    dataVersion: snap.data_version,
    engineVersion: snap.engine_version,
    wasmHash: snap.wasm_hash,
    snapshotJson: JSON.stringify(snap.snapshot),
    distances: dist.map((r) => [r.from_id, r.to_id, r.distance_km]),
    cities: snap.cities,
    homeCityId: null,
    cachedAt: new Date().toISOString(),
  };
  await saveProvisioning(blob);
  return blob;
}

let provisionedPromise: Promise<Provisioned | null> | null = null;

/** The engine + cached data, or null if never provisioned (offline first run). */
export function ensureProvisioned(): Promise<Provisioned | null> {
  if (provisionedPromise === null) {
    provisionedPromise = build();
  }
  return provisionedPromise;
}

async function build(): Promise<Provisioned | null> {
  const blob = (await loadProvisioning()) ?? (await fetchProvisioningOnline());
  if (blob === null || blob === undefined) {
    return null; // never been online → not provisioned
  }
  const engine = await getEngine();
  return {
    engine,
    snapshotJson: blob.snapshotJson,
    distanceFor: makeDistanceLookup(blob.distances),
    version: blob.version,
    homeCityId: blob.homeCityId,
  };
}

/** Re-pull the snapshot + distance matrix, then reset the singleton so the next
 *  ensureProvisioned() rebuilds against the fresh data. */
export async function refreshProvisioning(): Promise<void> {
  const blob = await fetchProvisioningOnline();
  if (blob !== null) {
    provisionedPromise = null;
  }
}

interface MetaResponse {
  active_tariff_version: string | null;
  wasm_hash: string | null;
  data_version?: string | null;
}

/** Compare the server's version tuple to the cached blob and re-provision if the
 *  tariff, engine binary, OR the reference data (cities/distances) changed —
 *  so a route/tariff edit (or a reseed) actually reaches the terminal. Best
 *  effort + online-only; a failure leaves the cache untouched. Call on startup
 *  of an authenticated shift and on reconnect. */
export async function ensureFreshProvisioning(): Promise<void> {
  const blob = await loadProvisioning();
  if (!blob) {
    return; // first-run provisioning is handled lazily by ensureProvisioned()
  }
  let meta: MetaResponse;
  try {
    const resp = await fetch("/api/v1/meta");
    if (!resp.ok) {
      return;
    }
    meta = (await resp.json()) as MetaResponse;
  } catch {
    return; // offline / unreachable — keep the cache
  }
  const stale =
    meta.active_tariff_version !== blob.version ||
    (meta.wasm_hash ?? null) !== blob.wasmHash ||
    (meta.data_version ?? null) !== blob.dataVersion;
  if (stale) {
    await refreshProvisioning();
  }
}
